<template>
  <section class="group-order-details-holder">
    <Loading v-if="!loadStatus" text="Loading..." />
    <template v-if="loadStatus && !Object.keys(groupOrderDetails).length">
      <h2 class="page-titles">{{ groupOrderDetails.name }}</h2>
    </template>
    <template v-if="loadStatus && Object.keys(groupOrderDetails).length">
      <section class="content-wrapper margin-top-4 mb-0">
        <div class="flex items-center justify-between">
          <div
            class="flex items-end gap-1 page-title-holder"
            :class="[
              isAllowListModalActive ||
              isRemoveRecipientModalActive ||
              showConfirmModal
                ? 'z-1'
                : 'z-10',
              isFinalized ? 'isFinalized' : '',
              isImpersonating && 'isImpersonating',
              isEditingName && 'isEditingName',
            ]"
          >
            <div
              class="page-title-holder__header"
              :class="{ isEditingName: isEditingName }"
            >
              <h2
                v-show="!isEditingName"
                class="page-titles mt-0 mb-0 text-left"
                :class="[
                  isAllowListModalActive ||
                  isRemoveRecipientModalActive ||
                  showConfirmModal
                    ? 'z-1'
                    : 'z-10',
                ]"
              >
                {{ groupOrderDetails.name }}
              </h2>
              <form @submit.prevent="handleGroupOrderNameEdit">
                <input
                  v-show="isEditingName"
                  type="text"
                  v-model="groupOrderDetails.name"
                  ref="groupOrderNameEditInput"
                  :disabled="!isEditingName"
                  class="page-titles mt-0 mb-0 w-max text-left"
                  :class="{ isEditable: isEditingName }"
                  @blur="handleBlur"
                />
              </form>
            </div>
            <button
              class="edit-icon"
              @click="handleActivateNameEdit"
              v-if="!isEditingName"
            >
              <img src="../../assets/edit.svg" alt="" />
            </button>
          </div>
          <template>
            <div
              class="flex-center gorder-actions"
              v-if="
                groupOrderDetails.state === 'pending' ||
                groupOrderDetails.state === 'lock_finalisation'
              "
              :class="{ 'is-allowListModal-active': isAllowListModalActive }"
            >
              <button
                v-if="isTeamOwner && groupOrderDetails.submissionsCount === 0"
                tag="button"
                class="whitebutton-gemnote edit-whitebutton-gemnote--small edit"
                @click="handleEditGroupOrder"
              >
                <span>Edit</span>
              </button>

              <button
                v-if="
                  groupOrderDetails.state === 'pending' ||
                  groupOrderDetails.state === 'lock_finalisation'
                "
                class="button-gemnote button-gemnote--small"
                :class="{ 'button-gemnote--disabled': finalizingGroupOrder }"
                :disabled="finalizingGroupOrder"
                @click="showFinalizeModal"
              >
                <span>Finalize</span>
              </button>
            </div>
            <div
              class="finalized-status finalized-desktop"
              v-if="groupOrderDetails.state === 'finalized' && !isFinalizing"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="9"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M4.68647 9c-.37 0-.74-.13063-1.02329-.38922L.42276 5.62233c-.56368-.51984-.56368-1.36492 0-1.88477.56657-.51985 1.48001-.51985 2.04369 0l2.22002 2.04473L10.5314.39189c.5665-.52252 1.48-.52252 2.0437 0 .5665.51984.5665 1.36226 0 1.88477L5.70977 8.61078C5.42648 8.86937 5.05648 9 4.68647 9Z"
                  fill="#76CC7A"
                />
              </svg>
              <p>Finalized</p>
            </div>
          </template>
        </div>
      </section>

      <div
        class="flex-center content-wrapper cta-holder mb-0 mt-0"
        :class="[
          isAllowListModalActive ||
          isRemoveRecipientModalActive ||
          showConfirmModal
            ? 'z-1'
            : 'z-10',
        ]"
      >
        <div class="arrow-link" @click="doCopy">
          {{ trimText(groupOrderDetails.recipientCollectionPaigeUrl, 40) }}
          <img src="../../assets/copy-icon.svg" alt="" />
        </div>
        <div
          class="finalizing-nav flex flex-col md:flex-row flex-wrap w-full md:w-max md:flex-nowrap items-center justify-center md:items-start md:justify-start"
          v-if="showFinalizingProgress && groupOrderDetails.state !== 'pending'"
        >
          <CircularProgress
            diameter="70"
            strokeWidth="4"
            innerStrokeWidth="4"
            totalSteps="100"
            :completedSteps="currentProgress"
            innerStrokeColor="#e2e2e2"
            startColor="#25282A"
            stopColor="#25282A"
          >
            <p>{{ currentProgress }}%</p>
          </CircularProgress>
          <div class="description">
            <h3>Finalizing</h3>
            <transition mode="out-in" name="slideY">
              <p class="state" :key="groupOrderDetails.state">
                {{ groupOrderDetails.state.replace(/_/g, ' ') }}
              </p>
            </transition>
          </div>
        </div>
        <div
          class="finalized-status finalized-mobile"
          v-if="groupOrderDetails.state === 'finalized' && !isFinalizing"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="9"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M4.68647 9c-.37 0-.74-.13063-1.02329-.38922L.42276 5.62233c-.56368-.51984-.56368-1.36492 0-1.88477.56657-.51985 1.48001-.51985 2.04369 0l2.22002 2.04473L10.5314.39189c.5665-.52252 1.48-.52252 2.0437 0 .5665.51984.5665 1.36226 0 1.88477L5.70977 8.61078C5.42648 8.86937 5.05648 9 4.68647 9Z"
              fill="#76CC7A"
            />
          </svg>
          <p>Finalised</p>
        </div>
      </div>

      <!-- Tabs Here -->
      <div
        class="tabs-wrapper"
        :class="{
          isFinalizing: showFinalizingProgress,
          isAllowListModalActive: isAllowListModalActive,
          isRemoveRecipientModalActive:
            isRemoveRecipientModalActive || showConfirmModal,
        }"
      >
        <Tabs
          :tabs="tabs"
          :isGroupOrder="true"
          @setActiveTab="setActiveTab($event)"
        />
      </div>

      <section
        class="content-wrapper tabs-content"
        :class="{
          isFinalizing: showFinalizingProgress,
          isImpersonating: isImpersonating,
        }"
      >
        <transition name="slide-fade">
          <GroupOrderSettingsTab
            v-if="tabs[0].isActive"
            @subscribed="setActiveTab($event)"
            :groupOrderDetails="groupOrderDetails"
          />
          <GroupOrderSummaryTab
            v-if="tabs[1].isActive"
            :groupOrderId="parseInt(id)"
            :groupOrderDetails="groupOrderDetails"
          />

          <GroupOrderRecipientListTab
            v-if="tabs[2].isActive"
            :groupOrderDetails="groupOrderDetails"
            :groupOrderId="parseInt(id)"
          />
        </transition>
      </section>

      <FinalizeModal
        :showFooter="true"
        :modalName="finalizeRedemptionModalName"
        v-show="isModalActive"
        @confirmAction="confirmFinalizeOrder($event)"
        buttonLabel="Finalize"
        colorVariation="coast"
      >
        <template slot="header" v-if="!showFinalizing">
          <h4 class="header">{{ modalHeader }}</h4>
        </template>

        <template slot="body" v-if="!showFinalizing">
          <div v-if="pendingRecipients.length">
            <p class="modal-description">
              {{ descriptionIntro }}.
              <!-- <span class="pending" @click="handleFinalize"> View Pending</span> -->
            </p>
            <p class="modal-description">
              These orders will all be skipped once you finalize.
            </p>
          </div>

          <p v-else class="modal-description">
            Finalizing will send all submissions to
            <span class="font-bold">Gemnote</span> for fulfillment and the
            redemption page URL will no longer be accessible to make further
            changes or receive subsequent submissions.
          </p>
        </template>
      </FinalizeModal>
      <!-- modal to show prgress of finalizing -->
      <FinalizeModal
        v-show="showFinalizing && groupOrderDetails.state !== 'finalized'"
        :showFooter="false"
        modalName="finalizing"
        colorVariation="coast"
        @hideModal="showFinalizing = false"
      >
        <template slot="header">
          <h4 class="header">Finalizing...</h4>
        </template>
        <template slot="body">
          <div class="progress-container">
            <p class="progress-amount">{{ currentProgress }}%</p>
            <div class="progress-track">
              <div
                class="width"
                :style="{ width: `${currentProgress}%` }"
              ></div>
            </div>
            <transition mode="out-in" name="slideY">
              <p class="state" :key="groupOrderDetails.state">
                {{ groupOrderDetails.state.replace(/_/g, ' ') }}
              </p>
            </transition>
            <p class="description">
              This is going to take us a few minutes. In a hurry? Feel free to
              leave this page and check back late
            </p>
          </div>
        </template>
      </FinalizeModal>
    </template>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { progressStatus, layouts, trimText } from '../../helpers'
import GroupOrderSettingsTab from '@/components/GroupOrder/GroupOrderSettingsTab'
import GroupOrderSummaryTab from '@/components/GroupOrder/GroupOrderSummaryTab'
import GroupOrderRecipientListTab from '@/components/GroupOrder/GroupOrderRecipientListTab'
import i18n from '@/internatinalization/i18n'

export default {
  name: 'GroupOrderDetails',
  props: ['id'],

  data() {
    return {
      tabs: [
        { name: 'Settings', isActive: true },
        { name: 'Summary', isActive: false },
        { name: 'Redemptions', isActive: false },
      ],
      finalizeRedemptionModalName: 'finalizeRedemptionPageModal',

      showFinalizing: false,
      isFinalizing: false,
      isEditingName: false,
      currentProgress: 1,
      groupOrderStates: [
        'validating_recipients',
        'validating_subscription',
        'checking_inventory',
        'creating_orders',
        'finalized',
      ],
      polling: null,
      trimText: trimText,
    }
  },

  components: {
    Tabs: () => import('../Universal/Tabs'),
    GroupOrderSettingsTab,
    GroupOrderSummaryTab,
    GroupOrderRecipientListTab,
    FinalizeModal: () => import('@/components/Universal/RemoveModal'),
    CircularProgress: () => import('@/components/Universal/CircularProgress'),
  },

  created() {
    this.$store.dispatch('groupOrder/getGroupOrder', this.id)
    this.$store.dispatch('groupOrder/getGroupOrdersRecipientsList', {
      groupOrderId: this.id,
      filterBy: 'Pending',
    })
    this.$store.dispatch('layout/setPageLayout', layouts.PAIGE_DASHBOARD)
  },
  mounted() {
    // this.$cable.subscribe({
    //   channel: 'WorkersChannel'
    // })
    this.watchOrderFinalization()
  },
  computed: {
    loadStatus() {
      return progressStatus(this.loadGroupOrder)
    },

    isRemoveRecipientModalActive() {
      return this.activeModals('removeRecipientsModal')
    },
    isFinalized() {
      return this.groupOrderDetails.state === 'finalized' && !this.isFinalizing
    },
    showFinalizingProgress() {
      return this.isFinalizing && !this.groupOrderDetails.isAutoFinalise
    },
    isImpersonating() {
      return this.userAuth.uid !== this.userProfile.email
    },
    isTeamOwner() {
      return (
        this.user && this.user.roles && this.user.roles.includes('team_owner')
      )
    },
    modalHeader() {
      return this.pendingRecipients && this.pendingRecipients.length
        ? 'Pending Recipient'
        : 'Finalize Order'
    },
    descriptionIntro() {
      const count = this.pendingRecipients && this.pendingRecipients.length
      return `There ${count == 1 ? 'is' : 'are'} ${count}  pending recipient(s)`
    },

    ...mapState({
      loadGroupOrder: (state) => state.groupOrder.loadGroupOrder,
      groupOrderDetails: (state) => state.groupOrder.groupOrderDetails,
      groupOrderStatus: (state) => state.groupOrder.groupOrderDetails.state,
      groupOrderTiers: (state) => state.groupOrder.groupOrderTiers,
      finalizingGroupOrder: (state) => state.groupOrder.finalizingGroupOrder,
      showConfirmModal: (state) => state.modal.showConfirmModal,
      showRemoveModal: (state) => state.modal.showRemoveModal,
      pendingRecipients: (state) =>
        state.groupOrder.groupOrderRecipientsList.filter(
          (recipient) => recipient.recipientState === 'Pending'
        ),
      user: (state) => state.profile.user,
      userAuth: (state) => state.auth.auth,
      userProfile: (state) => state.profile.user,
      isAllowListModalActive: (state) => state.modal.isAllowListModalActive,
    }),

    ...mapGetters({
      activeModals: 'modal/activeModals',
    }),

    isModalActive() {
      return this.activeModals(this.finalizeRedemptionModalName)
    },
    auth: () => this.$store.getters['auth/auth'],
  },
  channels: {
    WorkersChannel: {
      connected() {
        console.log('Connected')
      },
      rejected() {
        console.log('rejected')
      },
      received(data) {
        console.log(data)
        if (data.error) {
          this.showFinalizationError(data.error)
          return
        }
        if (data.state === 'lock_finalisation') {
          this.handleFinalizationError()
        } else {
          this.$store.commit('groupOrder/SET_GROUP_ORDER_STATE', data.state)
        }
      },
      disconnected() {
        console.log('disconnected')
      },
    },
  },
  watch: {
    'groupOrderDetails.state'(value) {
      if (this.currentProgress < 80) {
        this.groupOrderStates.forEach((state, index) => {
          // the position in the index will be used to calculate the general state progess
          if (this.groupOrderDetails.state === state) {
            this.currentProgress =
              ((index + 1) / this.groupOrderStates.length) * 100
          }
        })
      }
      if (value === 'finalized') {
        this.currentProgress += 100 - this.currentProgress
        setTimeout(() => {
          this.isFinalizing = false
          this.showFinalizing = false
        }, 1500)
      }
    },
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    handleEditGroupOrder() {
      this.$store.commit('groupOrder/CREATING_GROUP_ORDER_STEP', 1)
      this.$router.push({
        name: 'GroupOrderCreate',
      })
    },
    doCopy() {
      this.$copyText(this.groupOrderDetails.recipientCollectionPaigeUrl).then(
        () => {
          this.$store.dispatch('notification/pushNotification', {
            type: 'success',
            messages: [`Redemption page link copied`],
          })
        },
        (error) => {
          this.$store.dispatch('notification/pushNotification', {
            type: 'error',
            messages: [`Can not copy redemption page link.`],
          })
          console.log(error)
        }
      )
    },
    startPolling() {
      console.log('connected')
      this.polling = setInterval(() => {
        this.watchOrderFinalization()
        if (
          this.groupOrderStatus === 'finalized' ||
          this.groupOrderStatus === 'locked' ||
          this.groupOrderStatus === 'auto_finalising'
        ) {
          this.stopPolling()
        } else if (this.groupOrderStatus === 'lock_finalisation') {
          this.handleFinalizationError()
          this.stopPolling()
        } else {
          this.$store.commit(
            'groupOrder/SET_GROUP_ORDER_STATE',
            this.groupOrderStatus
          )
        }
      }, 6000)
    },
    stopPolling() {
      console.log('disconnected')
      clearInterval(this.polling)
    },
    watchOrderFinalization() {
      if (
        this.groupOrderDetails.state !== 'pending' &&
        this.groupOrderDetails.state !== 'finalized' &&
        this.groupOrderDetails.state !== 'lock_finalisation'
      ) {
        this.$store.dispatch('groupOrder/getGroupOrderState', this.id)
        this.groupOrderStates.forEach((state, index) => {
          // the position in the index will be used to calculate the general state progess
          if (this.groupOrderDetails.state === state) {
            this.currentProgress =
              ((index + 1) / this.groupOrderStates.length) * 100
          }
        })
        this.isFinalizing = true
      }
    },
    handleFinalize() {
      this.$router.replace({ query: { isPending: true } })
      this.setActiveTab({ name: 'Redemptions' })
      this.hideModal()
    },
    showFinalizeModal() {
      this.$store.dispatch(
        'modal/showRemoveModal',
        this.finalizeRedemptionModalName
      )
    },
    confirmFinalizeOrder() {
      this.$store.dispatch(
        'groupOrder/finalizeGroupOrder',
        this.groupOrderDetails.id
      )
      this.isFinalizing = true
      this.showFinalizing = true
    },
    setActiveTab(selectedTab) {
      this.tabs.forEach((tab) => {
        tab.isActive = tab.name === selectedTab.name
      })
    },
    hideModal() {
      this.$store.dispatch(
        'modal/hideRemoveModal',
        this.finalizeRedemptionModalName
      )
    },

    handleFinalizationError() {
      this.$store.dispatch(
        'notification/pushNotification',
        {
          type: 'error',
          messages: [i18n.t('grouporder.action.order-finalization-error')],
        },
        { root: true }
      )
    },

    showFinalizationError(message) {
      this.$store.dispatch(
        'notification/pushNotification',
        {
          type: 'error',
          messages: [message],
        },
        { root: true }
      )
    },
    async handleGroupOrderNameEdit() {
      if (!this.groupOrderDetails.name) {
        this.$store.dispatch(
          'notification/pushNotification',
          {
            type: 'error',
            messages: [i18n.t('validations.blank-grouporder-name')],
          },
          { root: true }
        )
        window.requestAnimationFrame(() =>
          this.$refs.groupOrderNameEditInput.focus()
        )
      } else {
        await this.$store.dispatch(
          'groupOrder/updateGroupOrder',
          this.groupOrderDetails
        )
        this.isEditingName = false
      }
    },

    handleActivateNameEdit() {
      this.isEditingName = !this.isEditingName
      window.requestAnimationFrame(() =>
        this.$refs.groupOrderNameEditInput.focus()
      )
    },

    async handleBlur() {
      if (this.groupOrderDetails.name) {
        await this.handleGroupOrderNameEdit()
        this.isEditingName = false
      } else {
        window.requestAnimationFrame(() =>
          this.$refs.groupOrderNameEditInput.focus()
        )
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.header {
  margin-bottom: 1.25rem;
  color: $pebble;
  font-family: $editorial;
  font-weight: normal;
  font-size: 2rem;
}

@media (max-width: 800px) {
  .header {
    line-height: 35px !important;
  }
}
.modal-description {
  font-size: 1rem;
  color: $pebble;
}

.pending {
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
}

.flex-center {
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.5rem;
    font-size: 0.813rem;
    width: 108px;

    &.edit {
      width: 83px;
    }
  }
}

.arrow-link {
  cursor: pointer;
  // margin-top: 1rem;
  border: none !important;
  text-align: left;
  padding-left: 0px;
  font-size: 0.813rem;
}

.gap {
  gap: 1rem;
  margin-top: 1rem;
}

.finalized-status {
  display: flex;
  align-items: center;
  background-color: #f9fdf4;
  padding: 8px 21px;

  &.finalized-mobile {
    display: none;
  }

  p {
    font-weight: 700;
    margin-left: 0.5rem;
  }
}

.finalizing-nav {
  display: flex;
  align-items: center;

  .state {
    text-transform: capitalize;
  }

  .description {
    margin-left: 1rem;
    text-align: left;

    h3 {
      font-size: 1rem;
      font-weight: 700;
    }

    p {
      font-size: 12px !important;
      font-weight: 600 !important;
    }
  }
}

.progress-container {
  text-align: center;

  .state {
    font-size: 12px;
    color: $pebble;
    font-weight: 700;
    text-transform: capitalize;
  }

  .description {
    margin-top: 2.438rem;
    color: $pebble;
  }

  .progress-amount {
    color: $pebble;
    text-align: right;
    font-weight: 700;
    font-size: 14px;
  }

  .progress-track {
    width: 100%;
    height: 0.563rem;
    background-color: #e6e6e6;

    .width {
      background-color: #24292c;
      height: 100%;
      transition: all 0.3s linear;
    }
  }
}
.edit-icon {
  padding-bottom: 0.8rem;
}

@media (max-width: 1100px) {
  .flex-center {
    flex-wrap: wrap;
  }
}

.slideY-enter-active,
.slideY-leave-active {
  transition: all 0.3s ease-out;
  transform: translateX(0);
}

.slideY-enter {
  opacity: 0;
  transform: translateY(10px);
}

.slideY-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.mb-0 {
  margin-bottom: 0px;
}

.mt-0 {
  margin-top: 0px;
}

.page-title-holder {
  .page-title-holder__header {
    h2 {
      white-space: nowrap;
      max-width: 500px;
      overflow: scroll;
      white-space: nowrap;
      scrollbar-width: none;
      -ms-overflow-style: none;
      background: transparent;
    }
    min-width: 30%;
    max-width: 90%;
    input {
      border: none;
    }
  }
}

.cta-holder {
  margin-top: 1rem;
}

.gorder-actions {
  gap: 10px !important;
}

@media (max-width: 630px) {
  .page-title-holder {
    position: fixed;
    left: 0;
    // top: 0rem;
    width: 100%;
    padding-top: 1.3rem;
    background-color: #f5f2ee !important;
    height: max-content;
    align-items: flex-end;
    justify-self: center;
    margin-left: 1rem;
    &.isEditingName {
      padding-top: 2.4rem;
      padding-bottom: 1.3rem;
    }

    &.isImpersonating {
      top: 4rem;
      margin-left: 0;
      padding-left: 1rem;
    }

    &.isFinalized {
      // padding-bottom: 1.3rem;
    }

    .page-title-holder__header {
      max-width: 80%;
      // min-width: max-content;

      &.isEditingName {
        min-width: 30%;
        max-width: 90%;
      }
    }
  }

  .tabs-wrapper {
    position: fixed;
    left: 0;
    top: 11.5rem;
    width: 100%;
    background-color: #f5f2ee;
    padding-top: 1rem;
    height: max-content;
    z-index: 10;

    &.isAllowListModalActive {
      z-index: 1;
    }
    &.isRemoveRecipientModalActive {
      z-index: -1;
    }

    &.isFinalizing {
      top: 17rem;
    }
  }

  .content-wrapper {
    &.tabs-content {
      position: relative;
      top: 11.5rem;
      z-index: 1;

      &.isImpersonating {
        top: -3rem;
      }
      &.isFinalizing {
        top: 17rem;
      }
    }
  }

  .cta-holder {
    margin-top: 0rem;
    position: fixed;
    top: 8rem;
    height: max-content;
    width: 100%;
    background-color: #f5f2ee !important;
    padding: 1rem 0;

    .arrow-link {
      margin-left: 1rem;
      margin-top: 0px;
    }
  }

  .finalized-status {
    margin-right: 1rem;

    &.finalized-mobile {
      display: flex;
    }
    &.finalized-desktop {
      display: none !important;
    }
  }

  .gorder-actions {
    background-color: #f5f2ee;
    border-top: 2px solid #514d43;
    z-index: 999;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 0.8rem 0;
    display: grid;
    grid-template-columns: 1fr;

    &.is-allowListModal-active {
      z-index: 1;
    }

    button {
      width: 90%;
      height: 3rem;
      margin: 0px auto;
      &.edit {
        width: 90%;
      }
    }
  }

  .flex-center {
    flex-wrap: nowrap;
  }

  .group-order-details-holder {
    height: 0px;
  }
}

.gorder-actions-footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;

  button {
    height: 3rem;
  }
}

@media (max-width: 350px) {
  .tabs-wrapper {
    position: fixed;
    left: 0;
    top: 9rem;
    width: 100%;
    background-color: #f5f2ee;
    padding-top: 1rem;
    height: max-content;
  }

  .content-wrapper {
    &.tabs-content {
      position: relative;
      top: 16rem;
      z-index: 1;
      padding-bottom: 9rem;
      width: 90%;
    }
  }
}
</style>
